import { useCallback, useEffect, useState } from 'react'
import { AspenPatientTasksService, CancelablePromise, PaginatedDTO_PatientTaskDTO_ } from '../client'

export const usePatientPatientTasks = (patientId: string) => {
    const [patientTasks, setPatientTasks] = useState<PaginatedDTO_PatientTaskDTO_ | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const [initialTaskIds, setInitialTaskIds] = useState<Set<string>>(new Set())
    const [hasInitialLoad, setHasInitialLoad] = useState(false)

    const fetchPatientTasks = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response: CancelablePromise<PaginatedDTO_PatientTaskDTO_> = AspenPatientTasksService.indexPatientTasksGet({
                patientId,
                sort: 'created_dttm:desc',
                limit: 100
            })
            const data = await response
            setPatientTasks(data)
        } catch (err) {
            setError('Failed to fetch patient tasks data')
        } finally {
            setLoading(false)
        }
    }, [patientId])

    useEffect(() => {
        let isMounted = true

        const initialFetch = async () => {
            setLoading(true)
            setError(null)
            try {
                const response: CancelablePromise<PaginatedDTO_PatientTaskDTO_> = AspenPatientTasksService.indexPatientTasksGet({
                    patientId,
                    sort: 'created_dttm:desc',
                    limit: 100
                })
                const data = await response
                if (isMounted) {
                    setPatientTasks(data)
                    // Store initial task IDs
                    const taskIds = new Set(data.data.map((task) => task.taskId))
                    setInitialTaskIds(taskIds)
                    setHasInitialLoad(true)
                }
            } catch (err) {
                if (isMounted) {
                    setError('Failed to fetch patient tasks data')
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        initialFetch()

        return () => {
            isMounted = false
        }
    }, [patientId])

    // Check if a task is new (not in initial load)
    const isNewTask = useCallback(
        (taskId: string) => {
            return hasInitialLoad && !initialTaskIds.has(taskId)
        },
        [hasInitialLoad, initialTaskIds]
    )

    return { patientTasks, loading, error, refetch: fetchPatientTasks, isNewTask }
}
